import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { subDays } from 'date-fns';

export interface BehaviorType {
  id: string;
  name: string;
  name_el?: string;
  name_en?: string;
  name_fr?: string;
  description?: string;
  description_el?: string;
  description_en?: string;
  description_fr?: string;
  category: 'positive' | 'negative';
  score_impact: number;
  icon?: string;
  color?: string;
  is_active: boolean;
}

export interface BehaviorRecord {
  id: string;
  guard_id: string;
  behavior_type_id: string;
  team_id?: string;
  recorded_by: string;
  incident_date: string;
  description?: string;
  evidence?: string;
  score_applied: number;
  status: 'active' | 'reversed' | 'disputed';
  created_at: string;
  updated_at: string;
  notes: any;
  behavior_type?: BehaviorType;
  guard?: {
    first_name: string;
    last_name: string;
    full_name?: string;
  };
  recorded_by_profile?: {
    first_name: string;
    last_name: string;
    full_name?: string;
  };
}

// Hook to fetch behavior types
export const useBehaviorTypes = () => {
  return useQuery({
    queryKey: ['behavior-types'],
    queryFn: async (): Promise<BehaviorType[]> => {
      const { data, error } = await supabase
        .from('behavior_types')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching behavior types:', error);
        throw error;
      }

      return (data || []).map(item => ({
        ...item,
        category: item.category as 'positive' | 'negative',
      }));
    },
    staleTime: 0, // Always fetch fresh data
  });
};

// Hook to fetch behavior records
export const useBehaviorRecords = (options?: {
  guardId?: string;
  teamId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['behavior-records', options],
    queryFn: async (): Promise<BehaviorRecord[]> => {
      let query = supabase
        .from('behavior_records')
        .select(`
          *,
          behavior_type:behavior_types(*),
          guard:profiles!guard_id(
            first_name,
            last_name,
            full_name
          ),
          recorded_by_profile:profiles!recorded_by(
            first_name,
            last_name,
            full_name
          )
        `)
        .order('incident_date', { ascending: false });

      if (options?.guardId) {
        query = query.eq('guard_id', options.guardId);
      }

      if (options?.teamId) {
        query = query.eq('team_id', options.teamId);
      }

      if (options?.startDate) {
        query = query.gte('incident_date', options.startDate);
      }

      if (options?.endDate) {
        query = query.lte('incident_date', options.endDate);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching behavior records:', error);
        throw error;
      }

      return (data || []).map(record => ({
        ...record,
        status: record.status as 'active' | 'reversed' | 'disputed',
        behavior_type: record.behavior_type ? {
          ...record.behavior_type,
          category: record.behavior_type.category as 'positive' | 'negative',
        } : undefined,
      }));
    },
    staleTime: 1 * 60 * 1000, // 1 minute - allow fresh data to show quickly
  });
};

// Hook to create behavior record
export const useCreateBehaviorRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      guardId,
      behaviorTypeId,
      teamId,
      incidentDate,
      description,
      evidence,
    }: {
      guardId: string;
      behaviorTypeId: string;
      teamId?: string;
      incidentDate: string;
      description?: string;
      evidence?: string;
    }) => {
      // First, get the behavior type to calculate score
      const { data: behaviorType, error: behaviorError } = await supabase
        .from('behavior_types')
        .select('score_impact')
        .eq('id', behaviorTypeId)
        .single();

      if (behaviorError) throw behaviorError;

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('behavior_records')
        .insert({
          guard_id: guardId,
          behavior_type_id: behaviorTypeId,
          team_id: teamId,
          recorded_by: user.id,
          incident_date: incidentDate,
          description,
          evidence,
          score_applied: behaviorType.score_impact,
          status: 'active',
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating behavior record:', error);
        throw error;
      }

      return data;
    },
    onSuccess: async (data) => {
      console.log('Behavior record created successfully, clearing ALL cache for guard:', data?.guard_id);
      
      // Nuclear option: Clear ALL cached queries
      await queryClient.clear();
      
      // Specifically invalidate behavior-types to force fresh data
      queryClient.invalidateQueries({ queryKey: ['behavior-types'] });
      
      // Force immediate refresh with correct dates
      if (data?.guard_id) {
        console.log('Forcing complete cache reset and refetch for guard:', data.guard_id);
        
        // Calculate tomorrow's date for the query
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowDate = tomorrow.toISOString().split('T')[0];
        const startDate = subDays(new Date(), 30).toISOString().split('T')[0];
        
        console.log('Using date range for refetch:', { startDate, endDate: tomorrowDate });
        
        // Force a direct database call to get fresh data
        try {
          const { data: freshData } = await supabase.rpc('calculate_guard_performance_with_base_score', {
            p_guard_id: data.guard_id,
            p_start_date: startDate,
            p_end_date: tomorrowDate,
          });
          
          console.log('Fresh data from database after behavior creation:', freshData);
        } catch (error) {
          console.error('Error fetching fresh performance data:', error);
        }
        
        // Wait a moment then trigger refetch
        setTimeout(() => {
          queryClient.refetchQueries({ 
            queryKey: ['base-score-performance'],
            exact: false
          });
        }, 1000);
      }

      toast.success('Συμπεριφορά καταγράφηκε επιτυχώς!');
    },
    onError: (error: any) => {
      console.error('Behavior record creation failed:', error);
      toast.error(`Σφάλμα καταγραφής συμπεριφοράς: ${error.message || 'Άγνωστο σφάλμα'}`);
    },
  });
};

// Hook to update behavior record
export const useUpdateBehaviorRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recordId,
      updates,
    }: {
      recordId: string;
      updates: Partial<BehaviorRecord>;
    }) => {
      const { data, error } = await supabase
        .from('behavior_records')
        .update(updates)
        .eq('id', recordId)
        .select()
        .single();

      if (error) {
        console.error('Error updating behavior record:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast.success('Συμπεριφορά ενημερώθηκε επιτυχώς!');
      queryClient.invalidateQueries({ queryKey: ['behavior-records'] });
      queryClient.invalidateQueries({ queryKey: ['performance-evaluations'] });
    },
    onError: (error: any) => {
      console.error('Behavior record update failed:', error);
      toast.error(`Σφάλμα ενημέρωσης συμπεριφοράς: ${error.message || 'Άγνωστο σφάλμα'}`);
    },
  });
};

// Hook to delete behavior record
export const useDeleteBehaviorRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recordId: string) => {
      const { error } = await supabase
        .from('behavior_records')
        .delete()
        .eq('id', recordId);

      if (error) {
        console.error('Error deleting behavior record:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast.success('Συμπεριφορά διαγράφηκε επιτυχώς!');
      queryClient.invalidateQueries({ queryKey: ['behavior-records'] });
      queryClient.invalidateQueries({ queryKey: ['performance-evaluations'] });
    },
    onError: (error: any) => {
      console.error('Behavior record deletion failed:', error);
      toast.error(`Σφάλμα διαγραφής συμπεριφοράς: ${error.message || 'Άγνωστο σφάλμα'}`);
    },
  });
};